import { memo } from "react";
import { MarkdownReact, MESSAGE_PROSE_CLASS } from "@/components/MarkdownReact";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { OutputAsset } from "@forgepoint/shared";
import { AssetDisplay } from "@/components/AssetDisplay";
import { MessageTimestamp } from "@/components/MessageTimestamp";

interface WorkflowRunViewAssistantMessageProps {
  id: string;
  senderName?: string;
  content?: string;
  assets?: OutputAsset[];
  timestamp?: Date;
}

/**
 * Renders an assistant text message with optional asset attachments. Memoized so
 * unchanged messages skip re-rendering (and markdown re-parse) when the thread
 * list re-renders during polling.
 */
export const WorkflowRunViewAssistantMessage = memo(
  function WorkflowRunViewAssistantMessage({
    senderName,
    content,
    assets,
    timestamp,
  }: WorkflowRunViewAssistantMessageProps) {
    const outputAssets = assets ?? [];

    return (
      <div className="flex items-start gap-3 w-full">
        <Avatar className="w-8 h-8">
          <AvatarFallback>{senderName?.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium">{senderName}</div>
            {timestamp && <MessageTimestamp timestamp={timestamp} />}
          </div>
          <div className={`mt-1 ${MESSAGE_PROSE_CLASS}`}>
            <MarkdownReact>{content}</MarkdownReact>
          </div>
          {outputAssets.length > 0 && (
            <div className="mt-2">
              <AssetDisplay assets={outputAssets} />
            </div>
          )}
        </div>
      </div>
    );
  },
);
